import React, { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/pro-regular-svg-icons";

import ViewerItemsVisibilityProvider from "@/services/context/viewer/items-visibility/viewer-items-visibility-provider.tsx";
import ViewerContextProvider from "@/services/context/viewer/instance/viewer-context-provider";
import ModelingToolsContextProvider from "@/services/context/viewer/modeling-tools/modeling-tools-context-provider.tsx";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import useProjectContext from "@/services/context/project/use-project-context.ts";

import useMeasureOverviewLoaders from "./hooks/use-measure-overview-loaders.ts";
import useMeasureProjectActions from "../../hooks/use-measure-project-actions.tsx";

import SimpleLoadingIndicator from "@/components/simple-loading-indicator";
import MeasureOverview3dViewerContainer from "./components/measure-overview-3d-viewer-container.tsx";
import MeasureCards from "./components/measure-cards-overview.tsx";
import MeasureCard3DViewerActions from "./components/measure-actions-cards.tsx";
import FlowMode from "@/models/enums/flow-mode.ts";
import thermalHighlight from "./styles/thermal-highlight.module.css";
import shellHighlight from "./styles/shell-highlight.module.css";
import scaleHighlight from "./styles/scale-highlight.module.css";
import MeasureOverviewTree from "@/pages/takeoff/layouts/measures/layouts/overview/components/measure-overview-tree.tsx";
import MeasureOverviewToolbar from "./components/measure-overview-toolbar.tsx";
import useMeasureOverviewTools from "./hooks/use-measure-overview-tools.ts";

const MeasureOverviewContent: React.FC = () => {
    const { project } = useProjectContext();

    const [isDragging, setIsDragging] = useState(false);

    const dragCounterRef = useRef<number>(0);
    const onFileDropRef = useRef<((files: FileList | null) => void) | null>(null);

    const viewerLoaders = useMeasureOverviewLoaders();
    const viewerTools = useMeasureOverviewTools();

    const {
        setupLaunch,
        launchPlanDetectionJobs,
        updateProjectSurfaces,
        launchProdJob,
        dialogs
    } = useMeasureProjectActions();

    return (
        <ViewerContextProvider loaders={viewerLoaders} tools={viewerTools}>
            <div
                className='relative w-full h-full overflow-hidden'
                onDragOver={(e) => {
                    if (project.flowMode !== FlowMode._2D3D) return;
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onDragEnter={(e) => {
                    if (project.flowMode !== FlowMode._2D3D) return;
                    e.preventDefault();
                    e.stopPropagation();
                    dragCounterRef.current += 1;
                    if (!isDragging) setIsDragging(true);
                }}
                onDragLeave={(e) => {
                    if (project.flowMode !== FlowMode._2D3D) return;
                    e.preventDefault();
                    e.stopPropagation();
                    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
                    if (dragCounterRef.current === 0) setIsDragging(false);
                }}
                onDrop={(e) => {
                    if (project.flowMode !== FlowMode._2D3D) return;
                    e.preventDefault();
                    e.stopPropagation();
                    dragCounterRef.current = 0;
                    setIsDragging(false);
                    onFileDropRef.current?.(e.dataTransfer.files);
                }}>
                <div className={`${thermalHighlight.scope} ${shellHighlight.scope} ${scaleHighlight.scope} absolute inset-4 flex justify-between z-1 pointer-events-none`}>
                    
                    <div className='flex flex-row gap-x-4'>
                        <div className='flex flex-col justify-between gap-y-4'>
                            <MeasureCards
                                flowMode={project.flowMode}
                                onRegisterFileSelect={(handler) => {
                                    onFileDropRef.current = handler;
                                }} />

                            <MeasureOverviewTree/>
                        </div>

                        <MeasureOverviewToolbar />
                    </div>
 
                    <MeasureCard3DViewerActions
                        setupLaunch={setupLaunch}
                        onLaunchPlanDetection={launchPlanDetectionJobs}
                        onCalculateSurfaces={updateProjectSurfaces}
                        onLaunchProdJob={launchProdJob}/>
                </div>

                {project.flowMode !== FlowMode._2D3D ? (
                    <MeasureOverview3dViewerContainer />
                    ) : (
                        <div className='absolute inset-0'>
                            <div className='w-full h-full' style={{backgroundColor: "#e4ede9"}}></div>
                        </div>
                    )
                }

                <div className={`absolute inset-4 flex border-2 border-dashed rounded-lg border-blue-500 bg-blue-300 pointer-events-none z-10 ${isDragging ? 'opacity-80' : 'opacity-0'}`}>
                    <div className='m-auto flex flex-row gap-x-8 text-white items-center'>
                        <FontAwesomeIcon icon={faDownload} className='text-3xl'/>
                        <div className='text-xl'>Déposer votre fichier</div>
                    </div>
                </div>

                {dialogs}

            </div>
        </ViewerContextProvider>
    );
};

const MeasureOverviewLayout: React.FC = () => {
    const { isLoading } = useProjectMeasureContext();

    return isLoading ? (
        <SimpleLoadingIndicator text='Chargement des données de métrés' />
    ) : (
        <ViewerItemsVisibilityProvider>
            <ModelingToolsContextProvider>
                <MeasureOverviewContent />
            </ModelingToolsContextProvider>
        </ViewerItemsVisibilityProvider>
    );
};

export default MeasureOverviewLayout;